import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DocumentSnapshot } from '@angular/fire/firestore';
import { ImageUploadService, UploadProgress } from '../../../../../services/image-upload.service';
import { MediaService, MediaDocument } from '../../../../../services/media.service';

interface MediaFile {
  name: string;
  url: string;
  fullPath: string;
  size: number;
  contentType: string;
  timeCreated: string;
}

const PAGE_SIZE = 24;

@Component({
  selector: 'app-media-selector-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './media-selector-modal.component.html',
  styleUrls: ['./media-selector-modal.component.scss']
})
export class MediaSelectorModalComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Output() onSelect = new EventEmitter<string>();
  @Output() onClose = new EventEmitter<void>();

  filteredFiles: MediaFile[] = [];
  loading = false;
  currentPageIndex = 0;
  hasNextPage = false;
  hasPrevPage = false;

  uploading = false;
  uploadProgress = 0;
  uploadStatus = '';

  private lastDoc: DocumentSnapshot | null = null;
  private pageSnapshots: (DocumentSnapshot | null)[] = [null];

  constructor(
    private imageUploadService: ImageUploadService,
    private mediaService: MediaService
  ) {}

  async ngOnInit() {
    if (this.show) {
      await this.loadFirstPage();
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['show']?.currentValue && !changes['show']?.previousValue) {
      this.currentPageIndex = 0;
      this.pageSnapshots = [null];
      this.lastDoc = null;
      await this.loadFirstPage();
    }
  }

  private async loadFirstPage() {
    this.loading = true;
    try {
      const result = await this.mediaService.getMediaFiles(PAGE_SIZE);
      this.filteredFiles = result.files
        .filter(f => f.contentType.startsWith('image/'))
        .map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === PAGE_SIZE;
      this.hasPrevPage = false;
    } catch (e) {
      console.error('Error loading media files:', e);
    } finally {
      this.loading = false;
    }
  }

  private toMediaFile(doc: MediaDocument & { id: string }): MediaFile {
    return {
      name: doc.name,
      url: doc.webp1400Url || doc.originalUrl,
      fullPath: doc.storagePath,
      size: doc.size,
      contentType: doc.contentType,
      timeCreated: doc.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
    };
  }

  async nextPage() {
    if (!this.hasNextPage || !this.lastDoc) return;
    this.loading = true;
    try {
      this.currentPageIndex++;
      if (this.pageSnapshots.length <= this.currentPageIndex) {
        this.pageSnapshots.push(this.lastDoc);
      }
      const result = await this.mediaService.getMediaFiles(PAGE_SIZE, this.lastDoc);
      this.filteredFiles = result.files
        .filter(f => f.contentType.startsWith('image/'))
        .map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === PAGE_SIZE;
      this.hasPrevPage = true;
    } catch (e) {
      console.error('Error loading next page:', e);
      this.currentPageIndex--;
    } finally {
      this.loading = false;
    }
  }

  async prevPage() {
    if (this.currentPageIndex <= 0) return;
    this.currentPageIndex--;
    this.loading = true;
    try {
      const cursor = this.pageSnapshots[this.currentPageIndex] || undefined;
      const result = await this.mediaService.getMediaFiles(PAGE_SIZE, cursor);
      this.filteredFiles = result.files
        .filter(f => f.contentType.startsWith('image/'))
        .map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === PAGE_SIZE;
      this.hasPrevPage = this.currentPageIndex > 0;
    } catch (e) {
      console.error('Error loading previous page:', e);
    } finally {
      this.loading = false;
    }
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    try {
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadStatus = 'Uploaden...';

      const variants = await this.imageUploadService.uploadImageWithVariants(file, (progress: UploadProgress) => {
        this.uploadStatus = progress.status;
        this.uploadProgress = progress.progress;
      });

      // Prepend uploaded image to current grid
      const timestamp = Date.now();
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const newFile: MediaFile = {
        name: `${timestamp}-${baseName}-original`,
        url: variants.webp1400 || variants.original,
        fullPath: `media/${timestamp}-${baseName}-original`,
        size: file.size,
        contentType: file.type,
        timeCreated: new Date().toISOString()
      };
      this.filteredFiles = [newFile, ...this.filteredFiles];

      this.uploading = false;
      this.uploadStatus = '';
      this.uploadProgress = 0;
      input.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      this.uploadStatus = 'Upload mislukt';
      this.uploading = false;
    }
  }

  selectImage(file: MediaFile) {
    this.onSelect.emit(file.url);
    this.onClose.emit();
  }

  close() {
    this.onClose.emit();
  }

  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }
}
